import {useTranslation} from 'react-i18next';

import type {ComboboxProps} from '@/components/ui/combobox';
import {Combobox} from '@/components/ui/combobox';
import {Typography} from '@/components/ui/typography';
import {graphqlClient} from '@/services/graphqlClient';
import type {JobRole} from '@/services/graphqlHooks';
import {useGetJobRolesQuery} from '@/services/graphqlHooks';
import {localeCompareSort} from '@/utils/localeCompareSort';

import JobRoleColorDot from './JobRoleColorDot';

export type JobRoleOption = JobRole & {
  value: string;
  label: string;
  keywords: string[];
};

export type JobRoleInputProps = Omit<ComboboxProps<JobRoleOption>, 'items'> & {
  value: string;
  onChange: (value: string) => void;
};

export type JobRoleMultiInputProps = Omit<
  ComboboxProps<JobRoleOption>,
  'items' | 'multiple'
> & {
  value: string[];
  onChange: (value: string[]) => void;
};

function useJobRoleOptions() {
  const {data, isLoading} = useGetJobRolesQuery(graphqlClient);

  const jobRoleOptions = [...(data?.getJobRoles || [])]
    .sort((roleA, roleB) => localeCompareSort(roleA.name, roleB.name))
    .map(jobRole => ({
      keywords: [jobRole.name],
      label: jobRole.name,
      value: jobRole.id,
      ...jobRole,
    })) as JobRoleOption[];

  return {jobRoleOptions, isLoading};
}

const renderJobRoleOption = (option: JobRoleOption) => (
  <div className="flex w-full items-center gap-2">
    <JobRoleColorDot color={option.color} className="size-3" />
    <Typography className="grow truncate">{option.label}</Typography>
  </div>
);

const JobRoleInput = ({value, onChange, ...props}: JobRoleInputProps) => {
  const {jobRoleOptions, isLoading} = useJobRoleOptions();

  const selectedItem =
    jobRoleOptions.find(option => option.value === value) || null;

  return (
    <Combobox
      items={jobRoleOptions}
      isLoading={isLoading}
      itemToString={option => option?.label || ''}
      selectedItem={selectedItem}
      onSelectedItemChange={({selectedItem}) => {
        onChange(selectedItem?.value || '');
      }}
      renderItem={renderJobRoleOption}
      {...props}
    />
  );
};

export const JobRoleMultiInput = ({
  value,
  onChange,
  ...props
}: JobRoleMultiInputProps) => {
  const {t} = useTranslation();
  const {jobRoleOptions, isLoading} = useJobRoleOptions();
  const toSelectedItem = (id: string) =>
    jobRoleOptions.find(option => option.value === id) ??
    ({id, value: id, label: t('Deleted job role')} as JobRoleOption);
  const selectedItems = isLoading ? [] : value.map(toSelectedItem);

  return (
    <Combobox
      items={jobRoleOptions}
      isLoading={isLoading}
      itemToString={option => option?.label || ''}
      itemToKey={option => option?.value}
      multiple={{
        selectedItems,
        onSelectedItemsChange: ({selectedItems}) => {
          onChange(selectedItems?.map(item => item.value) || []);
        },
      }}
      isItemSelected={item => value.includes(item.value)}
      isItemDisabled={item => value.includes(item.value)}
      renderItem={renderJobRoleOption}
      {...props}
    />
  );
};

export default JobRoleInput;
